import { z } from 'zod';
import { OperationIdSchema } from '../../ids';
import { InventorySchema, ObjectIdSchema } from '../../models/inventory';
import { ItemIdSchema } from '../../models/items';
import { SurveyKnowledgeSchema } from '../../models/survey';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** Which items the survey works with (server rules): the copy, the blank it is made on. */
export const SurveyItemsSchema = z.object({ map: ItemIdSchema, blank: ItemIdSchema });
export type SurveyItems = z.infer<typeof SurveyItemsSchema>;

/**
 * What the character knows of the map's wealth (RES-DISTRIBUTION D3): what it surveyed and what it
 * read on survey maps, never the wealth itself (OC-2). `gridM` is the side of a field's cell;
 * `items` names the survey map and the blank map, for the panels that copy and read.
 */
export const getSurveyKnowledge = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/survey`,
  access: 'session',
  response: z.object({
    knowledge: SurveyKnowledgeSchema,
    gridM: z.number().int().positive(),
    items: SurveyItemsSchema,
  }),
});

/**
 * Copies what the character knows within a region onto a blank map of the backpack (SD-6, SD-7):
 * the blank map is spent and a survey map, a unique object, lands in the backpack. `not_found` when
 * it knows nothing there. Idempotent.
 */
export const copySurvey = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/survey/maps`,
  access: 'session',
  body: z.object({ operationId: OperationIdSchema, region: z.string().min(1).max(64) }),
  response: z.object({
    operationId: OperationIdSchema,
    map: ObjectIdSchema,
    inventory: InventorySchema,
  }),
});

/**
 * Reads a survey map of the backpack (technical 6): what it says is added to what the character
 * knows, never improving a margin it knew better. The map stays. Idempotent.
 */
export const readSurvey = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/survey/read`,
  access: 'session',
  body: z.object({ operationId: OperationIdSchema, map: ObjectIdSchema }),
  response: z.object({ operationId: OperationIdSchema, knowledge: SurveyKnowledgeSchema }),
});
